/**
 * Impersonation — Admin-User-Verwaltung mit "Als User einloggen" (v1-Paritaet)
 *
 * Sichert die eigenen Admin-Tokens in sessionStorage, solange eine
 * Impersonation-Session aktiv ist. Eine aktive Session wird an der Existenz
 * von BACKUP_EMAIL_KEY erkannt — das ist der einzige Marker, den sowohl
 * AdminUsersPage (setzt ihn) als auch ImpersonationBanner (liest ihn) brauchen.
 */
import { useAuthStore } from '../stores/authStore'

export const BACKUP_ACCESS_KEY = 'ht-admin-backup-access-token'
export const BACKUP_REFRESH_KEY = 'ht-admin-backup-refresh-token'
export const BACKUP_EXPIRES_KEY = 'ht-admin-backup-token-expires-at'
export const BACKUP_EMAIL_KEY = 'ht-admin-backup-email'

export function isImpersonating(): boolean {
  try {
    return sessionStorage.getItem(BACKUP_EMAIL_KEY) !== null
  } catch {
    return false
  }
}

/** Sichert die aktuelle (Admin-)Session, bevor auf einen Ziel-User umgeschaltet wird. */
export function backupAdminSession(adminEmail: string): void {
  const { accessToken, expiresAt, getRefreshToken } = useAuthStore.getState()
  try {
    if (accessToken) sessionStorage.setItem(BACKUP_ACCESS_KEY, accessToken)
    const refreshToken = getRefreshToken()
    if (refreshToken) sessionStorage.setItem(BACKUP_REFRESH_KEY, refreshToken)
    if (expiresAt !== null) sessionStorage.setItem(BACKUP_EXPIRES_KEY, String(expiresAt))
    sessionStorage.setItem(BACKUP_EMAIL_KEY, adminEmail)
  } catch {
    // ignore — Storage nicht verfuegbar (z.B. Private-Mode-Quota)
  }
}

/** Stellt die gesicherte Admin-Session wieder her und loescht die Backup-Keys. */
export function restoreAdminSession(): void {
  try {
    const accessToken = sessionStorage.getItem(BACKUP_ACCESS_KEY)
    const refreshToken = sessionStorage.getItem(BACKUP_REFRESH_KEY)
    const expiresAtRaw = sessionStorage.getItem(BACKUP_EXPIRES_KEY)
    if (accessToken) {
      const expiresAt = expiresAtRaw ? Number(expiresAtRaw) : undefined
      const expiresIn =
        expiresAt !== undefined ? Math.max(0, Math.round((expiresAt - Date.now()) / 1000)) : undefined
      useAuthStore.getState().setTokens(accessToken, refreshToken ?? undefined, expiresIn)
    }
  } finally {
    clearImpersonationBackup()
  }
}

export function clearImpersonationBackup(): void {
  try {
    sessionStorage.removeItem(BACKUP_ACCESS_KEY)
    sessionStorage.removeItem(BACKUP_REFRESH_KEY)
    sessionStorage.removeItem(BACKUP_EXPIRES_KEY)
    sessionStorage.removeItem(BACKUP_EMAIL_KEY)
  } catch {
    // ignore
  }
}
